import { memo } from 'react'

/** Small inline spinner */
const Spinner = () => (
  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
)

/**
 * EntityCheckInPanel - Vendor check-in toggle + close-time picker.
 * Only rendered when canEditEntity is true (enforced by parent).
 * @atomic @idempotent
 */
const EntityCheckInPanel = memo(function EntityCheckInPanel({
  isLive,
  checkInLoading,
  handleCheckInToggle,
  liveUntilInput,
  onLiveUntilChange,
  addHoursToInput,
  handleUpdateLiveUntil,
  showUpdateButton,
  t,
}) {
  return (
    <div className="space-y-1.5">
      <button
        type="button"
        onClick={handleCheckInToggle}
        onTouchEnd={handleCheckInToggle}
        disabled={checkInLoading}
        className={[
          'w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-offset-2',
          isLive
            ? 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 focus:ring-green-300'
            : 'bg-primary hover:bg-primary-600 text-white focus:ring-primary',
          checkInLoading ? 'opacity-60 cursor-wait' : '',
        ].join(' ')}
      >
        <span className={['w-2 h-2 rounded-full flex-shrink-0', isLive ? 'bg-green-500 animate-pulse' : 'bg-white/70 border border-white/40'].join(' ')} />
        {checkInLoading
          ? t('common.saving', 'Saving…')
          : isLive
          ? t('checkin.checkOut', "I'm closed")
          : t('checkin.checkIn', "I'm open now")}
      </button>

      {/* Close-time picker */}
      <div className="flex items-center gap-2">
        <label className="text-xs text-gray-400 flex-shrink-0">
          {isLive ? t('checkin.updateCloseTime', 'Update close time:') : t('checkin.setCloseTime', 'Close at:')}
        </label>
        <input
          type="time"
          value={liveUntilInput}
          onChange={(e) => onLiveUntilChange(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-primary/60"
        />
        {[1, 2, 3].map(h => (
          <button
            key={h}
            type="button"
            onClick={(e) => { e.stopPropagation(); addHoursToInput(h) }}
            onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); addHoursToInput(h) }}
            className="text-[11px] px-1.5 py-1 rounded border border-gray-200 text-gray-500 hover:text-primary hover:border-primary/40 transition-colors"
          >+{h}h</button>
        ))}
      </div>

      {showUpdateButton && (
        <button
          type="button"
          disabled={checkInLoading}
          onClick={handleUpdateLiveUntil}
          onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); handleUpdateLiveUntil(e) }}
          className="w-full text-xs py-1.5 rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors disabled:opacity-60"
        >
          {checkInLoading ? t('common.saving', 'Saving…') : t('checkin.updateCloseTime', 'Update close time')}
        </button>
      )}
    </div>
  )
})

export default EntityCheckInPanel
